import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, AlertCircle, Upload } from "lucide-react";

interface MedinaCountyTextParserProps {
  onRecordsSaved?: () => void;
}

interface ImportResult {
  success: number;
  failed: number;
  failedRecords: Array<{ line: number; error: string }>;
}

export const MedinaCountyTextParser = ({ onRecordsSaved }: MedinaCountyTextParserProps) => {
  const [pastedText, setPastedText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const parseRecords = (text: string) => {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    const records = [];
    
    // Process in groups of 6 lines per record
    for (let i = 0; i < lines.length; i += 6) {
      if (i + 5 >= lines.length) break; // Skip incomplete records
      
      const documentNumber = lines[i];
      const documentType = lines[i + 1];
      const filingDateTime = lines[i + 2];
      const grantorName = lines[i + 3];
      const granteeName = lines[i + 4];
      const legalDescription = lines[i + 5];
      
      // Parse filing date from format "12/31/2024 10:19:01 AM"
      const dateMatch = filingDateTime.match(/(\d{1,2}\/\d{1,2}\/\d{4})/);
      let filingDate = new Date().toISOString().split('T')[0];
      
      if (dateMatch) {
        const [month, day, year] = dateMatch[1].split('/');
        filingDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      
      records.push({
        county: 'medina',
        document_number: documentNumber,
        document_type: documentType === 'LC' ? 'LAND CONTRACT' : documentType,
        filing_date: filingDate,
        grantor_name: grantorName,
        grantee_name: granteeName,
        legal_description: legalDescription === 'Multiple Legals: See Record' ? null : legalDescription,
        is_potential_owner_finance: true,
        confidence_score: 95,
        address_extraction_method: 'manual_import',
        status: 'new',
        pdf_downloaded: false,
        pdf_parsed: false,
        ocr_attempted: false,
        address_needs_review: true,
        grantor_address: null,
        grantee_address: null,
        property_address: null
      });
    }
    
    return records;
  };

  const processText = async (text: string) => {
    if (!text.trim()) {
      toast.error('Please paste the record data first');
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setImportResult(null);

    try {
      const records = parseRecords(text);
      
      if (records.length === 0) {
        toast.error('No valid records found in the pasted text');
        setIsProcessing(false);
        return;
      }

      console.log('Attempting to insert', records.length, 'records');
      
      const result: ImportResult = {
        success: 0,
        failed: 0,
        failedRecords: []
      };

      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        setProgress(Math.round(((i + 1) / records.length) * 100));

        try {
          // Check for duplicate
          const { data: existing } = await supabase
            .from('county_research')
            .select('id')
            .eq('county', 'medina')
            .eq('document_number', record.document_number)
            .maybeSingle();

          if (existing) {
            console.log(`Skipping duplicate: ${record.document_number}`);
            result.failed++;
            result.failedRecords.push({
              line: i + 1,
              error: 'Duplicate record'
            });
            continue;
          }

          const { error } = await supabase
            .from('county_research')
            .insert(record);

          if (error) {
            console.error('Insert error for record', i, ':', error);
            result.failed++;
            result.failedRecords.push({
              line: i + 1,
              error: error.message
            });
          } else {
            result.success++;
          }
        } catch (err) {
          console.error('Error processing record', i, ':', err);
          result.failed++;
          result.failedRecords.push({
            line: i + 1,
            error: String(err)
          });
        }
      }

      setImportResult(result);
      
      if (result.success > 0) {
        toast.success(`Successfully imported ${result.success} records`);
        setPastedText('');
        onRecordsSaved?.();
      }
      
      if (result.failed > 0) {
        toast.warning(`${result.failed} records failed to import`);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result as string;
      await processText(text);
    };
    reader.readAsText(file);
  };

  const handlePasteImport = () => {
    processText(pastedText);
  };

  return (
    <div className="space-y-4">
      {importResult && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Import completed: {importResult.success} succeeded, {importResult.failed} failed
            {importResult.failedRecords.length > 0 && (
              <div className="mt-2 text-sm">
                Failed records: {importResult.failedRecords.map(r => `Line ${r.line}`).join(', ')}
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Medina County Text Import</CardTitle>
          <CardDescription>
            Paste the copied text from Medina County search results. Each record should have 6 lines:
            Document Number, Document Type, Filing Date/Time, Grantor, Grantee, Legal Description.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Upload Text File</label>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept=".txt"
                onChange={handleFileUpload}
                disabled={isProcessing}
                className="block w-full text-sm"
              />
              <Upload className="h-4 w-4" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Or Paste Text Data</label>
            <Textarea
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              placeholder="Paste Medina County records here..."
              className="min-h-[400px] font-mono text-sm"
              disabled={isProcessing}
            />
            <Button 
              onClick={handlePasteImport}
              disabled={isProcessing || !pastedText.trim()}
              className="w-full"
            >
              {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Import Pasted Data
            </Button>
          </div>

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-center text-muted-foreground">
                Processing... {progress}%
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Expected Format:</strong> Records with 6 lines each:
          <br />1. Document Number
          <br />2. Document Type (e.g., LC)
          <br />3. Filing Date/Time (e.g., 12/31/2024 10:19:01 AM)
          <br />4. Grantor (Seller)
          <br />5. Grantee (Buyer)
          <br />6. Legal Description
        </AlertDescription>
      </Alert>
    </div>
  );
};